import type { OutboxOp } from './db';
import type { Lead, Visit, Objection, FeatureRequest } from '../../../shared/types';
import { getDb } from './db';
import { reconcileDependencies } from './dependencies';
import { storeEvents } from './store';
export interface RecoveryArchive {
  ops: OutboxOp[];
  lead?: Lead;
  visits: Visit[];
  objections: Objection[];
  requests: FeatureRequest[];
}
export const discardParked = async (leadId: string): Promise<RecoveryArchive> => {
  const tx = (await getDb()).transaction(
    ['outbox', 'leads', 'visits', 'objections', 'feature_requests'],
    'readwrite',
  );
  const ops = await reconcileDependencies(tx.objectStore('outbox'));
  const archive: RecoveryArchive = {
    ops: ops.filter((o) => o.local_lead_id === leadId),
    lead: await tx.objectStore('leads').get(leadId),
    visits: await tx.objectStore('visits').index('by_lead').getAll(leadId),
    objections: await tx.objectStore('objections').index('by_lead').getAll(leadId),
    requests: await tx.objectStore('feature_requests').index('by_lead').getAll(leadId),
  };
  for (const op of archive.ops)
    if (op.seq !== undefined) await tx.objectStore('outbox').delete(op.seq);
  await tx.objectStore('leads').delete(leadId);
  for (const row of archive.visits) await tx.objectStore('visits').delete(row.id);
  for (const row of archive.objections) await tx.objectStore('objections').delete(row.id);
  for (const row of archive.requests) await tx.objectStore('feature_requests').delete(row.id);
  await tx.done;
  storeEvents.dispatchEvent(new Event('change'));
  return archive;
};
/** Undo restores both rows and stable dependency IDs even if a snapshot landed meanwhile. */
export const restoreParked = async (a: RecoveryArchive): Promise<void> => {
  const tx = (await getDb()).transaction(
    ['outbox', 'leads', 'visits', 'objections', 'feature_requests'],
    'readwrite',
  );
  if (a.lead) await tx.objectStore('leads').put(a.lead);
  for (const row of a.visits) await tx.objectStore('visits').put(row);
  for (const row of a.objections) await tx.objectStore('objections').put(row);
  for (const row of a.requests) await tx.objectStore('feature_requests').put(row);
  const existing = new Set((await tx.objectStore('outbox').getAll()).map((o) => o.id));
  for (const { seq: _seq, ...op } of a.ops)
    if (!existing.has(op.id)) await tx.objectStore('outbox').add(op);
  await reconcileDependencies(tx.objectStore('outbox'));
  await tx.done;
  storeEvents.dispatchEvent(new Event('change'));
};
